import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AuthLayout, { useFormState } from '../components/AuthLayout'
import PasswordField from '../components/PasswordField'
import Alert from '../components/Alert'
import Spinner from '../components/Spinner'
import { useResendGate } from '../components/Countdown'
import { signup, resendConfirmEmail } from '../api/auth'
import { authErrorMessage, ApiError } from '../lib/errors'
import { setSession } from '../lib/tokenStore'

export default function Signup() {
  // No role on the public form: the account starts without one and an admin
  // assigns it in Settings → Approvals.
  const form = useFormState({ name: '', email: '', password: '', confirm: '' })
  const navigate = useNavigate()
  const [sent, setSent] = useState(false)
  const [resendAfter, setResendAfter] = useState(null)
  const [startedAt, setStartedAt] = useState(null)
  const resendLeft = useResendGate(resendAfter, startedAt)

  async function onResend() {
    form.setAlert(null)
    form.setBusy(true)
    try {
      const res = await resendConfirmEmail(form.values.email.trim())
      const data = res?.data || {}
      setResendAfter(Number.isFinite(Number(data.resend_after)) ? Number(data.resend_after) : null)
      setStartedAt(Date.now())
      form.setAlert({ type: 'success', message: 'We sent a new confirmation link.' })
    } catch (err) {
      form.setAlert({
        type: 'danger',
        message: authErrorMessage(err, 'Could not resend the link.'),
      })
    } finally {
      form.setBusy(false)
    }
  }

  async function onSubmit(e) {
    e.preventDefault()
    const errors = {}
    if (!form.values.name.trim()) errors.name = 'Name is required'
    if (!form.values.email.trim()) errors.email = 'Email is required'
    else if (!/@(utopia\.com|utopiabrands\.com|utopia\.pk)$/i.test(form.values.email.trim())) errors.email = 'Use your Utopia work email (@utopia.com, @utopiabrands.com or @utopia.pk)'
    if (!form.values.password) errors.password = 'Password is required'
    else if (form.values.password.length < 8) errors.password = 'Use at least 8 characters'
    if (form.values.confirm !== form.values.password) errors.confirm = 'Passwords do not match'
    form.setErrors(errors)
    if (Object.keys(errors).length) return

    form.setAlert(null)
    form.setBusy(true)
    try {
      const res = await signup(
        form.values.name.trim(),
        form.values.email.trim(),
        form.values.password,
      )
      // The account starts inactive; only the mailed link activates it, so the
      // tokens in this response are unusable until then.
      if (!res?.data?.is_active) {
        setSent(true)
        form.setAlert({
          type: 'success',
          message: `We sent a confirmation link to ${res?.data?.email || form.values.email.trim()}.`,
        })
        form.setBusy(false)
        return
      }
      setSession(res)
      navigate('/', { replace: true })
    } catch (err) {
      // 502 means the account was created but the email failed — offer a resend
      // rather than a dead end.
      if (err instanceof ApiError && err.status === 502) {
        setSent(true)
        form.setAlert({
          type: 'danger',
          message: 'Your account was created but we could not send the email. Try again below.',
        })
        form.setBusy(false)
        return
      }
      form.setAlert({
        type: 'danger',
        message: authErrorMessage(err, 'Could not create your account.'),
      })
      form.setBusy(false)
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="Open the link we sent. After that, an admin must approve your account before you can sign in."
        foot={
          <>
            Already confirmed?{' '}
            <Link className="link-btn" to="/auth/login">
              Sign in
            </Link>
          </>
        }
      >
        <Alert type={form.alert?.type}>{form.alert?.message}</Alert>

        {resendLeft > 0 ? (
          <p className="countdown" aria-live="polite">
            Resend available in {Math.ceil(resendLeft)}s
          </p>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={form.busy}
            onClick={onResend}
          >
            {form.busy ? <Spinner label="Sending link" /> : null}
            {form.busy ? 'Sending…' : 'Resend confirmation email'}
          </button>
        )}
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Create account"
      subtitle="Join TalentFlow for Utopia Brands hiring."
      foot={
        <>
          Already have an account?{' '}
          <Link className="link-btn" to="/auth/login">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <Alert type={form.alert?.type}>{form.alert?.message}</Alert>

        <div className="form-field">
          <label htmlFor="signup-name">
            Full name<span className="req"> *</span>
          </label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            value={form.values.name}
            onChange={(e) => form.setField('name', e.target.value)}
            className={form.errors.name ? 'err' : ''}
            placeholder="Asfand Ahmed"
            disabled={form.busy}
            required
          />
          <span className={`field-error${form.errors.name ? ' show' : ''}`}>
            {form.errors.name || ''}
          </span>
        </div>

        <div className="form-field">
          <label htmlFor="signup-email">
            Email<span className="req"> *</span>
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={form.values.email}
            onChange={(e) => form.setField('email', e.target.value)}
            className={form.errors.email ? 'err' : ''}
            placeholder="you@utopiabrands.com"
            disabled={form.busy}
            required
          />
          <span className={`field-error${form.errors.email ? ' show' : ''}`}>
            {form.errors.email || ''}
          </span>
        </div>

        <PasswordField
          id="signup-password"
          label="Password"
          autoComplete="new-password"
          value={form.values.password}
          onChange={(v) => form.setField('password', v)}
          error={form.errors.password}
          disabled={form.busy}
        />

        <PasswordField
          id="signup-confirm"
          label="Confirm password"
          autoComplete="new-password"
          value={form.values.confirm}
          onChange={(v) => form.setField('confirm', v)}
          error={form.errors.confirm}
          disabled={form.busy}
        />

        <p className="su-note">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
          We’ll email you a link to verify your address. An admin then approves your account and assigns your role.
        </p>

        <button type="submit" className="btn btn-primary btn-block" disabled={form.busy}>
          {form.busy ? <Spinner label="Creating account" /> : null}
          {form.busy ? 'Creating…' : 'Create account'}
        </button>
      </form>
    </AuthLayout>
  )
}
